import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getOEMProcesses,
  createOEMProcess,
  updateOEMProcess,
  getOEMPartProcesses,
  createOEMPartProcess,
  updateOEMPartProcess,
  getOEMPartOtherProcesses,
  createOEMPartOtherProcess,
  updateOEMPartOtherProcess,
  getSpareProcesses,
  createSpareProcess,
  updateSpareProcess,
  getSparePartProcesses,
  createSparePartProcess,
  updateSparePartProcess,
  getSparePartOtherProcesses,
  createSparePartOtherProcess,
  updateSparePartOtherProcess,
  getMaterialOtherProcesses,
  createMaterialOtherProcess,
  updateMaterialOtherProcess,
  getMaterial2ndTierProcesses,
  createMaterial2ndTierProcess,
  updateMaterial2ndTierProcess,
  getChildPartProcesses,
  createChildPartProcess,
  updateChildPartProcess,
  getSupplier2nd,
  createSupplier2nd,
  updateSupplier2nd,
  getReturnScrap,
  createReturnScrap,
  updateReturnScrap,
  getReasonPriceChange,
  createReasonPriceChange,
  updateReasonPriceChange,
  type getMasterParams,
  type Master,
  getDimensions,
  getReturnScrapPrice,
  type UpdateBasePriceBody,
  updateScrapPrices,
  type CreateQuarterBody,
  createNewQuarterScrapPrice,
  createScrapPrices,
  type CreateBasePriceBody,
} from "@/api/masterService";
import { useQueryInvalidation } from "@/lib/queryInvalidation";

export const useGetDimensions = () => {
  return useQuery({
    queryKey: queryKeys.master.dimensions(),
    queryFn: getDimensions,
    staleTime: CACHE_TIME.MASTER_DATA,
  });
};

export const useGetOEMProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getOEMProcesses()],
    queryFn: () => getOEMProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateOEMProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createOEMProcess(),
    mutationFn: (params: Master) => createOEMProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMProcesses();
    },
  });
};

export const useUpdateOEMProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateOEMProcess(),
    mutationFn: (params: Master) => updateOEMProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMProcesses();
    },
  });
};

export const useGetOEMPartProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getOEMPartProcesses()],
    queryFn: () => getOEMPartProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateOEMPartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createOEMPartProcess(),
    mutationFn: (params: Master) => createOEMPartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMPartProcesses();
    },
  });
};

export const useUpdateOEMPartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateOEMPartProcess(),
    mutationFn: (params: Master) => updateOEMPartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMPartProcesses();
    },
  });
};

export const useGetOEMPartOtherProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getOEMPartOtherProcesses()],
    queryFn: () => getOEMPartOtherProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateOEMPartOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createOEMPartOtherProcess(),
    mutationFn: (params: Master) => createOEMPartOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMPartOtherProcesses();
    },
  });
};

export const useUpdateOEMPartOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateOEMPartOtherProcess(),
    mutationFn: (params: Master) => updateOEMPartOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateOEMPartOtherProcesses();
    },
  });
};

export const useGetSpareProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getSpareProcesses()],
    queryFn: () => getSpareProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateSpareProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createSpareProcess(),
    mutationFn: (params: Master) => createSpareProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSpareProcesses();
    },
  });
};

export const useUpdateSpareProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateSpareProcess(),
    mutationFn: (params: Master) => updateSpareProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSpareProcesses();
    },
  });
};

export const useGetSparePartProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getSparePartProcesses()],
    queryFn: () => getSparePartProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateSparePartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createSparePartProcess(),
    mutationFn: (params: Master) => createSparePartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSparePartProcesses();
    },
  });
};

export const useUpdateSparePartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateSparePartProcess(),
    mutationFn: (params: Master) => updateSparePartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSparePartProcesses();
    },
  });
};

export const useGetSparePartOtherProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getSparePartOtherProcesses()],
    queryFn: () => getSparePartOtherProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateSparePartOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createSparePartOtherProcess(),
    mutationFn: (params: Master) => createSparePartOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSparePartOtherProcesses();
    },
  });
};

export const useUpdateSparePartOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateSparePartOtherProcess(),
    mutationFn: (params: Master) => updateSparePartOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateSparePartOtherProcesses();
    },
  });
};

export const useGetMaterialOtherProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getMaterialOtherProcesses()],
    queryFn: () => getMaterialOtherProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateMaterialOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createMaterialOtherProcess(),
    mutationFn: (params: Master) => createMaterialOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialOtherProcesses();
    },
  });
};

export const useUpdateMaterialOtherProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateMaterialOtherProcess(),
    mutationFn: (params: Master) => updateMaterialOtherProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterialOtherProcesses();
    },
  });
};

export const useGetMaterial2ndTierProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getMaterial2ndTierProcesses()],
    queryFn: () => getMaterial2ndTierProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateMaterial2ndTierProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createMaterial2ndTierProcess(),
    mutationFn: (params: Master) => createMaterial2ndTierProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterial2ndTierProcesses();
    },
  });
};

export const useUpdateMaterial2ndTierProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateMaterial2ndTierProcess(),
    mutationFn: (params: Master) => updateMaterial2ndTierProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateMaterial2ndTierProcesses();
    },
  });
};

export const useGetChildPartProcesses = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getChildPartProcesses()],
    queryFn: () => getChildPartProcesses(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateChildPartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createChildPartProcess(),
    mutationFn: (params: Master) => createChildPartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateChildPartProcesses();
    },
  });
};

export const useUpdateChildPartProcess = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateChildPartProcess(),
    mutationFn: (params: Master) => updateChildPartProcess(params),
    onSuccess: () => {
      queryInvalidation.invalidateChildPartProcesses();
    },
  });
};

export const useGetSupplier2nd = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getSupplier2nd(), params],
    queryFn: () => getSupplier2nd(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateSupplier2nd = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createSupplier2nd(),
    mutationFn: (params: Master) => createSupplier2nd(params),
    onSuccess: () => {
      queryInvalidation.invalidateSupplier2nd();
    },
  });
};

export const useUpdateSupplier2nd = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateSupplier2nd(),
    mutationFn: (params: Master) => updateSupplier2nd(params),
    onSuccess: () => {
      queryInvalidation.invalidateSupplier2nd();
    },
  });
};

export const useGetReturnScrap = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: queryKeys.masterProcess.getReturnScrap(params),
    queryFn: () => getReturnScrap(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useGetReturnScrapPrice = (year: number) => {
  return useQuery({
    queryKey: queryKeys.masterProcess.getReturnScrapPrice(year),
    queryFn: () => getReturnScrapPrice(year),
    staleTime: CACHE_TIME.SHORT,
    enabled: !!year,
  });
};

export const useCreateReturnScrap = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createReturnScrap(),
    mutationFn: (params: Master) => createReturnScrap(params),
    onSuccess: () => {
      queryInvalidation.invalidateReturnScrap();
      queryInvalidation.invalidateReturnScrapPrice();
    },
  });
};

export const useUpdateReturnScrap = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateReturnScrap(),
    mutationFn: (params: Master) => updateReturnScrap(params),
    onSuccess: () => {
      queryInvalidation.invalidateReturnScrap();
      queryInvalidation.invalidateReturnScrapPrice();
    },
  });
};

export const useCreateReturnScrapPrice = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createReturnScrapPrice(),
    mutationFn: (params: CreateBasePriceBody) => createScrapPrices(params),
    onSuccess: () => {
      queryInvalidation.invalidateReturnScrap();
      queryInvalidation.invalidateReturnScrapPrice();
    },
  });
};

export const useUpdateReturnScrapPrice = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateReturnScrapPrice(),
    mutationFn: (params: UpdateBasePriceBody) => updateScrapPrices(params),
    onSuccess: () => {
      queryInvalidation.invalidateReturnScrap();
      queryInvalidation.invalidateReturnScrapPrice();
    },
  });
};

export const useAddQuarterReturnScrapPrice = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.addQuarterScrapPrice(),
    mutationFn: (params: CreateQuarterBody) =>
      createNewQuarterScrapPrice(params),
    onSuccess: () => {
      queryInvalidation.invalidateReturnScrap();
      queryInvalidation.invalidateReturnScrapPrice();
    },
  });
};

export const useGetReasonPriceChange = (
  params: getMasterParams,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.masterProcess.getReasonPriceChange(), params],
    queryFn: () => getReasonPriceChange(params),
    staleTime: CACHE_TIME.MASTER_DATA,
    enabled: enabled,
  });
};

export const useCreateReasonPriceChange = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.createReasonPriceChange(),
    mutationFn: (params: Master) => createReasonPriceChange(params),
    onSuccess: () => {
      queryInvalidation.invalidateReasonPriceChange();
    },
  });
};

export const useUpdateReasonPriceChange = () => {
  const queryInvalidation = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.masterProcess.updateReasonPriceChange(),
    mutationFn: (params: Master) => updateReasonPriceChange(params),
    onSuccess: () => {
      queryInvalidation.invalidateReasonPriceChange();
    },
  });
};
